import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import client from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { trackEvent, ANALYTICS_EVENTS } from "@/lib/analytics";

export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const { t } = useTranslation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const sessionId = new URLSearchParams(hash.replace("#", "")).get("session_id");
    // Login-page flow is invite-only: no account provisioning, unknown
    // emails route to /contact via /login?not_registered=1.
    const fromLogin = new URLSearchParams(window.location.search).get("from") === "login";

    if (!sessionId) {
      navigate(fromLogin ? "/login" : "/");
      return;
    }

    if (fromLogin) {
      client
        .post("/auth/emergent-google", { session_id: sessionId })
        .then((res) => {
          const user = res.data.user;
          if (user && setUser) setUser(user);
          window.history.replaceState(null, "", "/dashboard");
          navigate("/dashboard", { replace: true });
        })
        .catch((err) => {
          window.history.replaceState(null, "", "/login");
          const detail = err?.response?.data?.detail;
          if (detail === "not_registered") {
            navigate("/login?not_registered=1", { replace: true });
          } else {
            toast.error(t("auth.signInFailed", { defaultValue: "Sign-in failed. Please try again." }));
            navigate("/login", { replace: true });
          }
        });
      return;
    }

    client
      .post("/auth/session", { session_id: sessionId })
      .then((res) => {
        const user = res.data.user;
        setUser(user);
        window.history.replaceState(null, "", window.location.pathname);
        if (user.onboarding_complete) {
          navigate("/dashboard", { replace: true, state: { user } });
        } else {
          trackEvent(ANALYTICS_EVENTS.SIGNUP, { role: user.role || "pending" });
          navigate("/onboarding", { replace: true, state: { user } });
        }
      })
      .catch((err) => {
        window.history.replaceState(null, "", window.location.pathname);
        const detail = err?.response?.data?.detail;
        // Registration locked — redirect to waitlist and pass prefill via
        // router state so the visitor's email never appears in the URL bar.
        if (detail && typeof detail === "object" && detail.code === "REGISTRATION_LOCKED") {
          toast.info(detail.message || "Registration is currently invite-only.");
          navigate("/waitlist", {
            replace: true,
            state: { prefillEmail: detail.email || "", prefillName: detail.name || "" },
          });
          return;
        }
        toast.error(t("auth.signInFailed", { defaultValue: "Sign-in failed. Please try again." }));
        navigate("/", { replace: true });
      });
  }, [navigate, setUser, t]);

  return (
    <div className="flex min-h-screen items-center justify-center" data-testid="auth-callback-loading">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}
